import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import SEOHelmet from '../../components/SEOHelmet';

/**
 * Modern Minimalist Landing Page
 * Design: Two-tone blue color scheme with simple line icons
 * Primary Blue: #2563eb (vibrant blue)
 * Light Blue: #dbeafe (soft light blue)
 * 
 * Note: This is a design preview page - no auto-redirect for logged-in users
 */

const LandingModern: React.FC = () => {
  usePageTitle('CEC Track - Professional License Management Made Simple');
  const { user } = useAuth();

  // Removed auto-redirect to allow admins to preview the design
  // If you want to use this as the main landing page, uncomment below:
  // const navigate = useNavigate();
  // React.useEffect(() => {
  //   if (user) {
  //     navigate('/dashboard');
  //   }
  // }, [user, navigate]);

  const styles = {
    root: {
      '--primary-blue': '#2563eb',
      '--light-blue': '#dbeafe',
      '--text-dark': '#1e293b',
      '--text-light': '#64748b',
    } as React.CSSProperties,
  };

  return (
    <>
      <SEOHelmet
        title="[Design Preview] CEC Track - Professional Continuing Education Management"
        description="Internal design preview page - not for production use"
        keywords="continuing education, CEC tracking, license management, professional development, architect credits, engineer PDH, compliance tracking"
        canonical="https://cectrack.com/"
        noindex={true}
      />
      
      <div style={styles.root}>
        {/* Hero Section */}
        <section style={{
          background: 'var(--primary-blue)',
          color: 'white',
          padding: '120px 0 100px',
          position: 'relative'
        }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <h1 style={{ 
                  fontSize: '3.5rem', 
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  lineHeight: '1.1'
                }}>
                  License Management.<br/>Simplified.
                </h1>
                <p style={{ 
                  fontSize: '1.3rem', 
                  marginBottom: '2.5rem',
                  opacity: 0.95,
                  lineHeight: '1.6'
                }}>
                  Track continuing education credits across all your professional licenses in one place.
                </p>
                <div className="d-flex gap-3">
                  <Link 
                    to="/register" 
                    style={{
                      backgroundColor: 'white',
                      color: 'var(--primary-blue)',
                      padding: '14px 32px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      display: 'inline-block',
                      transition: 'transform 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    Start Free →
                  </Link>
                  <Link 
                    to="/login" 
                    style={{
                      backgroundColor: 'transparent',
                      color: 'white',
                      padding: '14px 32px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      display: 'inline-block',
                      border: '2px solid white'
                    }}
                  >
                    Sign In
                  </Link>
                </div>
              </div>
              <div className="col-lg-6 d-none d-lg-block">
                <div style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  borderRadius: '16px',
                  padding: '48px',
                  backdropFilter: 'blur(20px)',
                }}>
                  {['Multi-State Tracking', 'Certificate Storage', 'Compliance Reports', 'Renewal Alerts'].map((feature, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: idx === 3 ? '0' : '24px',
                      fontSize: '1.15rem'
                    }}>
                      <span style={{ marginRight: '16px', fontSize: '1.5rem' }}>✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section style={{
          padding: '100px 0',
          backgroundColor: 'white'
        }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: 'var(--text-dark)',
                marginBottom: '1rem'
              }}>
                Everything You Need
              </h2>
              <p style={{
                fontSize: '1.2rem',
                color: 'var(--text-light)',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Professional-grade tools designed for licensed professionals
              </p>
            </div>

            <div className="row g-4">
              {[
                { icon: '◯', title: 'Cycle Management', desc: 'Track renewal cycles for each state and profession' },
                { icon: '□', title: 'Class Tracking', desc: 'Log and categorize all your CE courses' },
                { icon: '△', title: 'Compliance Dashboard', desc: 'See your status at a glance' },
                { icon: '◇', title: 'Document Storage', desc: 'Securely store all certificates' },
                { icon: '⬡', title: 'Multi-State Support', desc: 'Manage licenses across all 50 states' },
                { icon: '⬢', title: 'Smart Reminders', desc: 'Never miss a deadline again' },
              ].map((feature, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div style={{
                    padding: '32px',
                    backgroundColor: 'var(--light-blue)',
                    borderRadius: '12px',
                    height: '100%',
                    transition: 'transform 0.2s',
                    cursor: 'default'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{
                      fontSize: '2.5rem',
                      color: 'var(--primary-blue)',
                      marginBottom: '16px'
                    }}>
                      {feature.icon}
                    </div>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: 'var(--text-dark)',
                      marginBottom: '12px'
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{
                      color: 'var(--text-light)',
                      fontSize: '1rem',
                      lineHeight: '1.6',
                      margin: 0
                    }}>
                      {feature.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Professions Section */}
        <section style={{
          padding: '100px 0',
          backgroundColor: 'var(--light-blue)'
        }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: 'var(--text-dark)',
                marginBottom: '1rem'
              }}>
                Built for Professionals
              </h2>
              <p style={{
                fontSize: '1.2rem',
                color: 'var(--text-light)'
              }}>
                Supporting multiple professions and jurisdictions
              </p>
            </div>

            <div className="row g-4 justify-content-center">
              {[
                'Architects',
                'Professional Engineers',
                'Landscape Architects',
                'Structural Engineers',
                'Interior Designers'
              ].map((profession, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div style={{
                    padding: '24px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    color: 'var(--text-dark)',
                    border: '1px solid rgba(37, 99, 235, 0.1)'
                  }}>
                    {profession}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section style={{
          padding: '100px 0',
          backgroundColor: 'white'
        }}>
          <div className="container">
            <div className="row text-center">
              {[
                { number: '50+', label: 'States Supported' },
                { number: '1000+', label: 'Active Users' },
                { number: '10K+', label: 'Credits Tracked' },
                { number: '99%', label: 'On-Time Renewals' }
              ].map((stat, idx) => (
                <div key={idx} className="col-6 col-md-3 mb-4 mb-md-0">
                  <div style={{
                    fontSize: '3rem',
                    fontWeight: '700',
                    color: 'var(--primary-blue)',
                    marginBottom: '8px'
                  }}>
                    {stat.number}
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: 'var(--text-light)',
                    fontWeight: '500'
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: '100px 0',
          backgroundColor: 'var(--primary-blue)',
          color: 'white',
          textAlign: 'center'
        }}>
          <div className="container">
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              marginBottom: '1.5rem'
            }}>
              Ready to Simplify Your Renewals?
            </h2>
            <p style={{
              fontSize: '1.25rem',
              marginBottom: '2.5rem',
              opacity: 0.95
            }}>
              Join hundreds of professionals managing their licenses with CEC Track
            </p>
            <Link 
              to="/register" 
              style={{
                backgroundColor: 'white',
                color: 'var(--primary-blue)',
                padding: '16px 40px',
                fontSize: '1.2rem',
                fontWeight: '600',
                borderRadius: '8px',
                textDecoration: 'none',
                display: 'inline-block',
                transition: 'transform 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Get Started Free
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandingModern;
